import React, { useCallback, useEffect, useState } from 'react';
import { batch, useDispatch } from "react-redux";
import { DndContext, closestCenter, useSensor, useSensors, PointerSensor, KeyboardSensor } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';

import { useTypedSelector } from '../../../../../../store/reducers/use-typed-selector';
import { toggleCustomizeFormModal, updateRegistrationSteps, updateRequiredQuestions } from '../../../../../../store/actions/admin/create-event';
import { addNewRegistrationQuestion, updateRegistrationQuestions } from '../../../../../../store/actions/admin/registration-questions';
import { UpdateRegistrationSteps } from '../../../../../../connection/create-event';
import { CreateRegQuestion, SaveRegistrationQuestion } from '../../../../../../connection/registration-questions';
import { RegistrationQuestion, CreateRegistrationQuestion, QuestionType } from '../../../../../../types/working-model';
import Modal from '../../../../../general-ui/modal/modal';
import { showAlert } from '../../../../../general-ui/alert/alert-service';
import WaitingIndicator from '../../../../../general-ui/waiting-indicator/waiting-indicator';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import { useFieldRequired } from '../../../../../../utils/registration-required-fields';
import SortableRegistrationQuestion from './sortable-registration-question';
import EditCustomQuestion from './custom-registration-question';


const RegistrationQuestionsModal: React.FC = () => {
	const customizeFormModalOpen = useTypedSelector(state => state.CreateEventReducer.customizeFormModalOpen);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const registrationStep = useTypedSelector(state => state.CreateEventReducer.registrationStep);
	const registrationQuestions = useTypedSelector(state => state.RegistrationQuestionsReducer.registrationQuestions);
	const token = useTypedSelector(state => state.AuthReducer.token);

	const isFieldRequired = useFieldRequired();
	const dispatch = useDispatch();

	const [selectedQuestions, setSelectedQuestions] = useState<RegistrationQuestion[]>([]);
	const [requiredQuestions, setRequiredQuestions] = useState<number[]>([]);
	const [editingQuestion, setEditingQuestion] = useState<RegistrationQuestion | CreateRegistrationQuestion | null>(null);
	const [saving, setSaving] = useState<boolean>(false);

	useEffect(() => {
		if (!workingEvent?.registration_steps) return;
		setRequiredQuestions(workingEvent.required_registration_questions ?? []);
		setSelectedQuestions(workingEvent.registration_steps.find(step => step.type === registrationStep)?.questions || []);
	}, [registrationStep, workingEvent?.required_registration_questions, workingEvent?.registration_steps]);

	// list of registration questions
	const sortedQuestions = (): RegistrationQuestion[] => {
		const globalQuestions: RegistrationQuestion[] = [];
		const customQuestions: RegistrationQuestion[] = [];

		const selectedQuestionMap = selectedQuestions.reduce((a: { [key: string]: string; }, c) => {
			a[c.registration_question] = c.name;
			return a;
		}, {});

		registrationQuestions.forEach((_question: RegistrationQuestion) => {
			// if this question is selected, dont do anything
			if (selectedQuestionMap?.[_question.registration_question]) return;
			if (_question.global) return globalQuestions.push(_question);
			customQuestions.push(_question);
		});

		return [selectedQuestions, globalQuestions, customQuestions].flat();
	};

	const selectedIds = selectedQuestions.map((question: RegistrationQuestion) => question.registration_question);

	// change question properties
	const setQuestionRequired = useCallback((question: number, value: number) => {
		const required = Boolean(value);
		if (required) {
			setRequiredQuestions([...requiredQuestions, question]);
		} else {
			if (isFieldRequired.includes(question)) {
				showAlert({
					message: "This field is required for other registration functions currently in use.",
					duration: 3000,
					type: "error",
				});
				return;
			}
			setRequiredQuestions(requiredQuestions.filter((_question: number) => _question !== question));
		}
	}, [requiredQuestions, isFieldRequired]);

	const handleToggleQuestion = useCallback((value: RegistrationQuestion, on: boolean) => {
		if (on) {
			setSelectedQuestions(Array.from(new Set([...selectedQuestions, value])));
		} else {
			if (isFieldRequired.includes(value.registration_question)) {
				showAlert({
					message: "This field is required for other registration functions currently in use.",
					duration: 3000,
					type: "error",
				});
				return;
			}
			setSelectedQuestions(selectedQuestions.filter((question) => question.registration_question !== value.registration_question));
		}
	}, [selectedQuestions, isFieldRequired]);

	// drag & sort
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const handleQuestionDragEnd = (event: any): void => {
		const _selectedQuestions = selectedQuestions.slice();

		const startIndex = _selectedQuestions.findIndex(_q => _q.registration_question.toString() === event.active.id);
		const newIndex = _selectedQuestions.findIndex(_q => _q.registration_question.toString() === event.over.id);

		const [question] = _selectedQuestions.splice(startIndex, 1);
		_selectedQuestions.splice(newIndex, 0, question);

		setSelectedQuestions(_selectedQuestions);
	};

	// create or edit custom questions
	const newQuestion = (): void => {
		setEditingQuestion({
			name: "Enter Question Title",
			type: QuestionType.text,
			options: null,
			persistent: false,
			default_value: null
		});
	};

	// finish up
	const handleCancel = (): void => {
		if (editingQuestion) setEditingQuestion(null);
		dispatch(toggleCustomizeFormModal(false));
	};

	const handleSave = async (): Promise<void> => {
		if (!workingEvent?.registration_steps) return;

		if (editingQuestion?.type === QuestionType.select && (!editingQuestion.options || editingQuestion.options.length === 0)) {
			showAlert({ message: "Cannot save dropdown", description: "Your dropdown list needs to contain options", duration: 3000, type: 'error' });
			return;
		}

		setSaving(true);

		if (editingQuestion) {
			if ("registration_question" in editingQuestion) {
				const saved = await SaveRegistrationQuestion(editingQuestion, token!);
				dispatch(updateRegistrationQuestions([saved]));
				setSelectedQuestions(questions => {
					return questions.map(question => {
						if (question.registration_question === saved.registration_question) {
							return editingQuestion;
						} else {
							return question;
						}
					});
				});
			} else {
				const question = await CreateRegQuestion(editingQuestion, token!);

				dispatch(addNewRegistrationQuestion(question));
				setSelectedQuestions(questions => {
					return [...questions, question];
				});
			}
			setEditingQuestion(null);
		} else {
			const updatedSteps = workingEvent.registration_steps.map(step => {
				if (step.type !== registrationStep) return step;
				return { ...step, questions: selectedQuestions };
			});
			batch(() => {
				dispatch(updateRegistrationSteps(updatedSteps));
				dispatch(updateRequiredQuestions(requiredQuestions));
			});
			await UpdateRegistrationSteps(token!, workingEvent.uuid, updatedSteps);
			dispatch(toggleCustomizeFormModal(false));
		}
		setSaving(false);
	};

	return (
		<Modal
			open={customizeFormModalOpen}
			onRequestClose={handleCancel}
			title={editingQuestion ? "Edit Question" : "Customize Form"}
			cancellable={false}
			closeable={false}
			size={"medium"}
			showOverflow
			footer={(
				<>
					<button onClick={handleCancel} style={{ marginRight: 16 }}>Cancel</button>
					<button onClick={handleSave} className="primary">{saving ? <WaitingIndicator /> : "Apply"}</button>
				</>
			)}
		>
			{editingQuestion ? (
				<EditCustomQuestion
					editingQuestion={editingQuestion}
					setEditingQuestion={setEditingQuestion}
					requiredQuestions={requiredQuestions}
					setQuestionRequired={setQuestionRequired}
				/>
			) : (
				<>
					<div className="registration-options-container">
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleQuestionDragEnd}
							modifiers={[restrictToParentElement]}
							autoScroll={false}
						>
							<SortableContext
								items={sortedQuestions().map(q => q.registration_question.toString()) ?? []}
								strategy={verticalListSortingStrategy}
							>
								{sortedQuestions().map((question: RegistrationQuestion, idx: number) => {
									return (
										<SortableRegistrationQuestion key={idx}
											{...{
												question,
												setEditingQuestion: () => setEditingQuestion(question),
												setQuestionRequired,
												selectedIds,
												id: question.registration_question.toString(),
												handleToggleQuestion,
												requiredQuestions
											}}
										/>
									);
								})}
							</SortableContext>
						</DndContext>
					</div>
					<button className="small" onClick={newQuestion}>
						<Icon name={ICONS.ADD} size={12} color={COLORS.WHITE} /><span>Custom Field</span>
					</button>
				</>
			)}
		</Modal>
	);
};

export default RegistrationQuestionsModal;